function calcMax(num) {
    // Check if the input is a positive integer
    if (Number.isInteger(num) && num > 0) {
        // Create a string with num 9s and convert it to an integer
        const result = parseInt('9'.repeat(num));
        return result;
    } else {
        // Handle invalid input
        //console.error("Invalid input. Please provide a positive integer.");
        return null;
    }
}

export function incrementEndingDigits(inputString) {
    // Use regular expression to match the trailing digits
    const regex = /(\d+)$/;
    const match = inputString.match(regex);

    if (match) {
      const digits = match[0];
      const incrementedDigits = (parseInt(digits) + 1).toString().padStart(digits.length, '0');
      const prefix = inputString.slice(0, match.index);
      return prefix + incrementedDigits;
    } else {
      // If no digits found at the end, return the original string
      return inputString;
    }
  }

// For a yearBased series whose year segment is not this year: the first value
// of this year (year replaced, counter reset to 1 padded to nDigits). Null when
// no rollover is due — not year-based, no year found, or already this year.
function rolledOverValue(nSerie) {
    if (!nSerie.yearBased) return null

    // Two-digit year (e.g. TKT-26-1001) vs four-digit year (e.g. INV-2026-1001)
    const fourDigitYearPattern = /(200[1-9]|20[1-9]\d|2[1-9]\d{2}|[3-9]\d{3})/
    const twoDigitYearPattern  = /(?<![0-9])(\d{2})(?![0-9])/

    const yearPattern = nSerie.twoDigitYear ? twoDigitYearPattern : fourDigitYearPattern
    const yearMatch = nSerie.nextValue.match(yearPattern)
    if (!yearMatch) return null

    const currentYear      = new Date().getFullYear()
    const currentYearValue = nSerie.twoDigitYear ? currentYear % 100 : currentYear
    if (parseInt(yearMatch[0]) === currentYearValue) return null

    const paddedOne  = '1'.padStart(nSerie.nDigits || 4, '0')
    const newYearStr = nSerie.twoDigitYear
        ? String(currentYearValue).padStart(2, '0')
        : String(currentYear)

    return nSerie.nextValue
        .replace(yearPattern, newYearStr)
        .replace(/\d+$/, paddedOne)
}

// Returns the next value for an nSerie, handling year rollover for yearBased series.
export function nextSerieValue(nSerie) {
    return rolledOverValue(nSerie) ?? incrementEndingDigits(nSerie.nextValue)
}

// The value to hand out NOW. Normally the stored nextValue, but a year-based
// series whose nextValue was set in an earlier year (the last number was
// issued in December, or the default in the settings file names a past year)
// starts this year's run instead of issuing a number with last year in it.
export function currentSerieValue(nSerie) {
    return rolledOverValue(nSerie) ?? nSerie.nextValue
}

// "FK-1042" -> ["FK-", 1042]; a value with no trailing digits has no position.
const splitSerieValue = (value) => {
    const match = String(value ?? '').match(/(\d+)$/)
    return match ? [value.slice(0, match.index), parseInt(match[0])] : [value, null]
}

/**
 * The series after `used` was issued from it: `lastValue` is the number just
 * used, `nextValue` the one to offer next.
 *
 * Issuing the offered number moves to the one after it. Issuing a HIGHER number
 * of the same series — typed over, or a jump to start a new range — moves past
 * it, so a number below one already used is never offered. Issuing a LOWER one
 * (filling a gap) or one of another format leaves the next number where it was.
 */
export function advanceSerie(nSerie, used) {
    const current = currentSerieValue(nSerie)
    const [currentPrefix, currentNo] = splitSerieValue(current)
    const [usedPrefix, usedNo] = splitSerieValue(used)
    const movesOn = usedPrefix === currentPrefix && usedNo !== null && currentNo !== null && usedNo >= currentNo
    return { ...nSerie, lastValue: used, nextValue: movesOn ? incrementEndingDigits(used) : current }
}

function analyzeSerie(inputString, entity) {
    // Define regular expressions for matching patterns
    const numericPattern = /\d+$/;
    const textPattern = /-INV-/;
    const fourDigitYearPattern = /(200[1-9]|20[1-9]\d|2[1-9]\d{2}|[3-9]\d{3})/;
    // Two-digit year: exactly 2 digits not adjacent to another digit (e.g. 26 in TKT-26-{N})
    const twoDigitYearPattern  = /(?<![0-9])(\d{2})(?![0-9])/;

    let baseSerie
    let yearBased    = false
    let twoDigitYear = false
    let valid        = false

    // Extract numeric values at the end of the string
    const numericMatch = inputString.match(numericPattern);
    const nDigits = numericMatch ? numericMatch[0].length : 0;
    if (nDigits > 2) { valid = true }
    baseSerie = inputString.replace(numericPattern, '{N}')

    const endNum = calcMax(nDigits)
    // Check if the text pattern exists in the string
    const textExists = textPattern.test(inputString);

    // Check for four-digit year first (e.g. 2026 in INV-2026-1001)
    const fourDigitYearMatch = baseSerie.match(fourDigitYearPattern);
    if (fourDigitYearMatch !== null) {
        baseSerie = baseSerie.replace(fourDigitYearPattern, '{Y}')
        yearBased = true
    }

    // Check for two-digit year if no four-digit year was found (e.g. 26 in TKT-26-{N})
    if (!yearBased) {
        const twoDigitYearMatch = baseSerie.match(twoDigitYearPattern);
        if (twoDigitYearMatch !== null) {
            baseSerie = baseSerie.replace(twoDigitYearPattern, '{Y}')
            yearBased    = true
            twoDigitYear = true
        }
    }

    // Extract the starting number (if numeric values are present)
    const startNum = numericMatch ? parseInt(numericMatch[0]) : null;

    // Create and return the result object
    const result = {
        id: `${entity}.${baseSerie}.${nDigits}`,
        uniqueNo : `${entity}.${inputString}`,
        entity: entity,
        baseSerie,
        nDigits: nDigits,
        startNum,
        endNum,
        nextValue:inputString,
        yearBased,
        twoDigitYear,
        valid
    };

    return result;
}

export default analyzeSerie
